<i18n src="./translations"></i18n>

<template>
  <div
    ref="pdpActions"
    class="pdp-actions"
    :class="classes"
    :data-preload="[$options.name]"
  >
    <template
      v-if="isPlpOrSearchPage || (isPurchasable && viewport !== 'mobile')"
    >
      <QuantitySelector
        class="pdp-actions__quantity"
        :has-label="false"
        :has-vertical-divider="false"
        :value="quantityAmount"
        :change-quantity="handleQuantityChange"
        :darker="true"
        :choices="quantityOptions"
      />
      <Button
        class="pdp-actions__in-the-cart"
        modifier="primary"
        :on-click="handleCartClick"
        v-bind="addToCartButtonProps"
      />
    </template>
    <Button
      class="pdp-actions__to-wishlist qa-addToWishlistButton"
      :class="{ 'pdp-actions__to-wishlist--single': !isPurchasable }"
      modifier="secondary"
      icon="wishlist_heart"
      :title="$t('pdp-actions.to_wishlist')"
      :on-click="handleWishlistClick"
    />
    <portal
      v-if="viewport === 'mobile' && isPurchasable && !isPlpOrSearchPage"
      to="product-cart-group"
    >
      <div class="pdp-actions__cart-group pdp-actions__cart-group--mobile">
        <QuantitySelector
          class="pdp-actions__quantity"
          :has-label="false"
          :has-vertical-divider="false"
          :change-quantity="handleQuantityChange"
          :darker="true"
          :choices="quantityOptions"
          :value="quantityAmount"
        />
        <Button
          class="pdp-actions__in-the-cart"
          modifier="primary"
          :on-click="handleCartClick"
          v-bind="addToCartButtonProps"
        />
      </div>
    </portal>
  </div>
</template>

<script>
import QuantitySelector from 'Components/01-atoms/quantity-selector/QuantitySelector';
import globalMixin from 'Libs/mixins/globalMixin';
import Button from 'Components/01-atoms/button/Button';
import { mapState } from 'vuex';
import get from 'lodash/get';

export default {
  name: 'PdpActions',
  components: { QuantitySelector, Button },
  mixins: [globalMixin],
  props: {
    quantityOptions: {
      default: () => [],
      type: Array,
    },
    quantityAmount: {
      default: 1,
      type: Number,
    },
    isPurchasable: {
      type: Boolean,
      default: true,
    },
    addToCartButtonProps: {
      default: () => ({
        class: '',
        title: '',
        icon: { name: '', height: '', width: '' },
        disabled: false,
      }),
      type: Object,
    },
  },
  data() {
    return {
      quantity: '1',
    };
  },
  computed: {
    ...mapState('core', { viewport: ({ viewport: { range } }) => range }),
    ...mapState('productsList', {
      isPlpOrSearchPage: (state) => {
        const type = get(state, 'data.plp.pageType');
        return (
          ['productListing', 'search', 'brandProductListing'].indexOf(type) !==
          -1
        );
      },
    }),
  },
  mounted() {
    this.quantity = this.quantityAmount;
  },
  methods: {
    handleQuantityChange({ quantity }) {
      this.quantity = quantity;
      this.$emit('quantity-change', quantity);
    },
    handleCartClick() {
      this.$emit('cart-click', this.quantity);
    },
    handleWishlistClick() {
      this.$emit('wishlist-click');
    },
  },
};
</script>

<!-- Scoped Styling -->
<style scoped lang="scss">
@import 'mixins';
@import 'variables/breakpoints';

.pdp-actions {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  --row-height: 48px;
}

.pdp-actions__quantity {
  margin: 0 var(--space-1) var(--space-1) 0;
  width: 82px;

  ::v-deep {
    .select {
      height: var(--row-height);
      padding-left: var(--space-2);
      border-color: var(--color-silver-lining) !important;
    }

    .icon {
      color: var(--color-secondary);
    }

    .input {
      height: var(--row-height);
      padding-top: 0;
    }
  }
}

::v-deep .button {
  .inner {
    display: flex;
  }

  .title {
    flex-basis: 100%;
    margin: 0;

    @include typography-size(var(--font-size-M), var(--font-size-MLL));
  }
}

.pdp-actions__in-the-cart {
  margin-bottom: var(--space-1);
  height: var(--row-height);
  flex-grow: 1;
}

.pdp-actions__to-wishlist {
  height: var(--row-height);
  flex-basis: 100%;
  color: var(--color-rhino-without-horn);
  border-color: var(--color-silver-lining);

  &--single {
    margin-top: 0;
  }

  ::v-deep {
    .wishlistSvg path {
      fill: var(--color-rhino-without-horn);
    }
  }
}

@media #{$_mediaMDown} {
  .pdp-actions__in-the-cart {
    ::v-deep {
      .inner {
        padding: 0 var(--space-1);
      }
    }
  }
}
</style>

<!-- Global Styling -->
<style lang="scss">
@import 'variables/breakpoints';

@media #{$_mediaMDown} {
  .cart-group-portal {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 99999;
    padding: var(--space-1) var(--space-2);
    background-color: var(--color-wild-sandish);
    box-shadow: 0 -3px 6px 0 rgba(0, 0, 0, 0.16);

    --row-height: 48px;

    .pdp-actions__cart-group {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-grow: 1;
    }
  }
}
</style>
